import { Components } from 'botframework-webchat';
import { FluentThemeProvider } from 'botframework-webchat-fluent-theme';
import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  CopilotStudioClient,
  CopilotStudioWebChat,
} from '@microsoft/agents-copilotstudio-client';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { useCopilotChat } from './copilotChatContext';
import botAvatarImage from '../../assets/logos/GNIECloud-Logo-500-White-Betel.png';

const { BasicWebChat, Composer } = Components;

/** Cool neutral “chat canvas” — distinct from app chrome (--dust-white / #F7EDE5) */
const CHAT_TRANSCRIPT_BG = '#EEF1F6';
const CHAT_BUBBLE_BORDER = '#D4DAE6';

const CHAT_STYLE_OPTIONS = {
  accent: '#002EE5',
  backgroundColor: CHAT_TRANSCRIPT_BG,
  subtle: '#050533',
  primaryFont:
    '"BrightstarSans-Regular", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  rootWidth: '100%',
  rootHeight: '100%',
  bubbleBackground: '#FFFFFF',
  bubbleTextColor: '#050533',
  bubbleBorderColor: CHAT_BUBBLE_BORDER,
  bubbleBorderWidth: 1,
  bubbleBorderRadius: 12,
  bubbleFromUserBackground: '#002EE5',
  bubbleFromUserTextColor: '#FFFFFF',
  bubbleFromUserBorderRadius: 12,
  sendBoxBackground: '#FFFFFF',
  sendBoxTextColor: '#050533',
  sendBoxPlaceholderColor: 'rgba(5, 5, 51, 0.45)',
  sendBoxButtonColor: '#002EE5',
  botAvatarBackgroundColor: '#002EE5',
  botAvatarImage,
  botAvatarInitials: '',

  // Quick-reply / suggested-action chips (e.g. “Contact Center”) — solid contrast vs transcript.
  suggestedActionBackgroundColor: '#FFFFFF',
  suggestedActionBorderColor: '#002EE5',
  suggestedActionBorderWidth: 1,
  suggestedActionTextColor: '#002EE5',
  suggestedActionBorderRadius: 20,
  suggestedActionBackgroundColorOnHover: '#E8EEFF',
  suggestedActionBorderColorOnHover: '#002EE5',
  suggestedActionTextColorOnHover: '#002EE5',
};

const AGENTS_SETTINGS = {
  appClientId: process.env.REACT_APP_COPILOT_CLIENT_ID,
  tenantId: process.env.REACT_APP_COPILOT_TENANT_ID,
  environmentId: process.env.REACT_APP_COPILOT_ENVIRONMENT_ID,
  agentIdentifier: process.env.REACT_APP_COPILOT_AGENT_IDENTIFIER,
  directConnectUrl:
    'https://d62ed6d0ea2bef13ac5091750beb05.19.environment.api.powerplatform.com/copilotstudio/dataverse-backed/authenticated/bots/crf98_globalFliedClientServices/conversations?api-version=2022-03-01-preview',
};

const WEBCHAT_SETTINGS = { showTyping: true };

let sharedMsalInstance = null;
let sharedMsalInitPromise = null;
let sharedTokenPromise = null;

function getMsalInstance(settings) {
  if (!sharedMsalInstance) {
    sharedMsalInstance = new PublicClientApplication({
      auth: {
        clientId: settings.appClientId,
        authority: `https://login.microsoftonline.com/${settings.tenantId}`,
      },
    });
  }
  if (!sharedMsalInitPromise) {
    sharedMsalInitPromise = sharedMsalInstance.initialize();
  }
  return {
    msalInstance: sharedMsalInstance,
    initPromise: sharedMsalInitPromise,
  };
}

function CopilotChatPanel() {
  const [connection, setConnection] = useState(null);

  async function acquireToken(settings) {
    if (sharedTokenPromise) {
      return sharedTokenPromise;
    }

    sharedTokenPromise = (async () => {
      const { msalInstance, initPromise } = getMsalInstance(settings);
      await initPromise;

      const loginRequest = {
        scopes: ['https://api.powerplatform.com/.default'],
        redirectUri: window.location.origin,
      };

      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          return response.accessToken;
        }
      } catch (e) {
        if (!(e instanceof InteractionRequiredAuthError)) {
          throw e;
        }
      }

      // Fall back to a single interactive sign-in, then retry silently.
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: loginResponse.account,
      });
      return response.accessToken;
    })();

    try {
      return await sharedTokenPromise;
    } finally {
      sharedTokenPromise = null;
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await acquireToken(AGENTS_SETTINGS);
      if (cancelled) return;
      const client = new CopilotStudioClient(AGENTS_SETTINGS, token);
      setConnection(
        CopilotStudioWebChat.createConnection(client, WEBCHAT_SETTINGS),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!connection) {
    return null;
  }

  return (
    <FluentThemeProvider>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          minHeight: 0,
          overflow: 'hidden',
          bgcolor: CHAT_TRANSCRIPT_BG,
        }}
      >
        <Composer directLine={connection} styleOptions={CHAT_STYLE_OPTIONS}>
          <BasicWebChat />
        </Composer>
      </Box>
    </FluentThemeProvider>
  );
}

function ChatPanelHeader({ onReset }) {
  const { close } = useCopilotChat();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        bgcolor: CHAT_TRANSCRIPT_BG,
        flexShrink: 0,
      }}
    >
      <IconButton
        onClick={close}
        aria-label='Close assistant'
        size='small'
        sx={{ color: 'var(--vega-blue)' }}
      >
        {isMobile ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />}
      </IconButton>
      <Typography
        variant='subtitle1'
        sx={{
          flex: 1,
          textAlign: 'center',
          fontFamily: 'BrightstarSans-Regular, sans-serif',
          color: 'var(--vega-blue)',
          fontWeight: 600,
        }}
      >
        Assistant
      </Typography>
      <IconButton
        onClick={onReset}
        aria-label='Reset conversation'
        size='small'
        sx={{ color: 'var(--vega-blue)' }}
      >
        <RestartAltIcon />
      </IconButton>
    </Box>
  );
}

function CopilotResizeHandle() {
  const { panelWidth, setPanelWidth } = useCopilotChat();

  const onMouseDown = useCallback(
    e => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = panelWidth;
      const onMove = e2 => {
        const max = Math.min(800, Math.floor(window.innerWidth * 0.65));
        const next = Math.min(
          max,
          Math.max(280, startW + (startX - e2.clientX)),
        );
        setPanelWidth(next);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [panelWidth, setPanelWidth],
  );

  return (
    <Box
      onMouseDown={onMouseDown}
      role='separator'
      aria-orientation='vertical'
      aria-label='Resize chat panel'
      sx={{
        width: 8,
        flexShrink: 0,
        alignSelf: 'stretch',
        minHeight: { md: '100vh' },
        cursor: 'col-resize',
        bgcolor: 'rgba(0, 0, 0, 0.06)',
        borderLeft: '1px solid rgba(255, 255, 255, 0.9)',
        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.12)' },
      }}
    />
  );
}

/** Right-edge pull tab (sidecart) — opens the assistant panel. */
function CopilotSidecartTab() {
  const { open } = useCopilotChat();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <IconButton
      onClick={open}
      aria-label='Open assistant chat'
      disableRipple
      sx={{
        position: 'fixed',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1250,
        width: isMobile ? 26 : 28,
        minWidth: isMobile ? 26 : 28,
        height: isMobile ? 72 : 88,
        px: 0,
        py: 0,
        borderRadius: '14px 0 0 14px',
        bgcolor: 'var(--reach-blue)',
        color: 'var(--dust-white)',
        boxShadow: '-4px 0 16px rgba(5, 5, 51, 0.28)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRight: 'none',
        '&:hover': {
          bgcolor: 'var(--vega-blue)',
        },
      }}
    >
      <ChevronLeftIcon sx={{ fontSize: isMobile ? 20 : 22 }} />
    </IconButton>
  );
}

export function CopilotChatDock() {
  const { isOpen, panelWidth } = useCopilotChat();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [webChatKey, setWebChatKey] = useState(0);

  const handleResetConversation = useCallback(() => {
    setWebChatKey(k => k + 1);
  }, []);

  if (!isOpen) {
    return <CopilotSidecartTab />;
  }

  if (isMobile) {
    return (
      <Box
        sx={{
          width: '100%',
          flexShrink: 0,
          height: 'min(50vh, 480px)',
          minHeight: 260,
          display: 'flex',
          flexDirection: 'column',
          borderTop: '2px solid #FFFFFF',
          boxShadow: '0 -4px 20px rgba(5, 5, 51, 0.1)',
          bgcolor: CHAT_TRANSCRIPT_BG,
        }}
      >
        <ChatPanelHeader onReset={handleResetConversation} />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CopilotChatPanel key={webChatKey} />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <CopilotResizeHandle />
      <Box
        sx={{
          width: panelWidth,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          minHeight: { md: '100vh' },
          height: { md: '100vh' },
          maxHeight: { md: '100vh' },
          borderLeft: '2px solid #FFFFFF',
          boxShadow: '-6px 0 24px rgba(5, 5, 51, 0.1)',
          bgcolor: CHAT_TRANSCRIPT_BG,
        }}
      >
        <ChatPanelHeader onReset={handleResetConversation} />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CopilotChatPanel key={webChatKey} />
        </Box>
      </Box>
    </>
  );
}
